// The state of one report form (deployment.md §7.6, R1312, R1323).
//
// Two surfaces file a report: the hub (`ui/hub/panels/report.ts`) and the
// game client's overlay. Each one draws its own markup in its own idiom,
// and both keep the same state: which piece of content the form is open
// under, the reason class the player picked, the note, and the pieces a
// report already went out for. This module holds that state, so the rule
// "SEND needs a reason, and a sent piece reads REPORTED" has one home.
//
// A target names a piece by what it IS (a line by its sequence number or
// line id, a commander by the account id) and never carries a copy of the
// words: a server reads the piece and stores its own copy.

import { oneOf } from '../util/oneof';
import { REPORT_REASONS } from '../gen/balance';
import { hubReport as S } from '../gen/strings';
import { wordOf } from '../locale/wordof';

/** One reason class of data/product/meta.toml [meta.moderation]. */
export type ReportReason = (typeof REPORT_REASONS)[number];

/** The label of one reason class. Every class of REPORT_REASONS has a
 *  `reason_<class>` sentence; the test of the hub form holds that. */
export function reasonLabel(reason: string): string {
  return wordOf(S, `reason_${reason}`, reason.toUpperCase());
}

/** The report a SEND press files. `reason` is '' while the player has
 *  picked no class, and SEND then refuses. */
export interface PendingReport<T> {
  key: string;
  target: T;
  reason: ReportReason | '';
  note: string;
}

/** The state of one report form. `T` is what the surface sends. */
export class ReportDraft<T> {
  /** The pieces a control was drawn for, by key, so a press finds its
   *  target without carrying the fields through the markup. */
  private readonly targets = new Map<string, T>();
  /** The keys a report went out for. Their control reads REPORTED. */
  private readonly done = new Set<string>();
  /** The piece the form is open under, or ''. */
  openKey = '';
  reason: ReportReason | '' = '';
  note = '';

  /** Records the target of one piece, as its control is drawn. */
  offer(key: string, target: T): void { this.targets.set(key, target); }

  /** Whether a report of the piece went out. */
  sent(key: string): boolean { return this.done.has(key); }

  /** Opens the form under a piece, or closes it when it is open there.
   *  Either way the reason and the note start empty. */
  toggle(key: string): void {
    this.openKey = this.openKey === key ? '' : key;
    this.reason = '';
    this.note = '';
  }

  /** Closes the form. */
  close(): void { this.openKey = ''; }

  /** Picks a reason class. Answers false for a class off the list. */
  pick(reason: string): boolean {
    const r = oneOf(REPORT_REASONS, reason);
    if (r === undefined) return false;
    this.reason = r;
    return true;
  }

  /** The report SEND would file, or null while no piece is open. */
  pending(): PendingReport<T> | null {
    const target = this.targets.get(this.openKey);
    if (this.openKey === '' || target === undefined) return null;
    return { key: this.openKey, target, reason: this.reason, note: this.note.trim() };
  }

  /** Marks a piece reported, and closes the form if it is open there. */
  markSent(key: string): void {
    this.done.add(key);
    if (this.openKey === key) this.openKey = '';
  }
}
